import React from 'react';
import { ChartTooltip } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { formatHourLabel } from './hourlyActivityMeta';

/**
 * Shape of the props Recharts injects when this component is used as a
 * `<Tooltip content>` element. `payload[i].payload.hour` is the original
 * bucket; `payload[i].color` (or `payload[i].payload.fill`) is the bar's
 * fill so the indicator picks up the exact wedge colour.
 */
export type HourlyActivityTooltipProps = {
    active?: boolean;
    payload?: ReadonlyArray<{
        color?: string;
        value?: number | string;
        payload?: { fill?: string; hour?: number };
    }>;
    valueFormatter?: (value: number | string) => React.ReactNode;
};

/**
 * Bar tooltip for the hourly chart. Labels the hovered bar with
 * `"HH:00"` and shows its currency-formatted revenue. Returns `null` when
 * inactive or the payload is empty so recharts can keep the tooltip
 * layer hidden.
 */
export const HourlyActivityTooltip: React.FC<HourlyActivityTooltipProps> = ({
    active,
    payload,
    valueFormatter,
}) => {
    if (!active || !payload?.length) {
        return null;
    }

    return (
        <ChartTooltip {...getNodeTestIdProp('hourly-activity-tooltip')}>
            {payload.map((entry, index) => {
                const hour = entry.payload?.hour;
                const label =
                    typeof hour === 'number'
                        ? `${formatHourLabel(hour)}:00`
                        : '';
                const indicatorColor = entry.color ?? entry.payload?.fill;

                return (
                    <ChartTooltip.Item
                        key={`${hour ?? ''}-${index}`}
                        {...getNodeTestIdProp(
                            `hourly-activity-tooltip-item-${index}`,
                        )}
                    >
                        <ChartTooltip.Indicator
                            color={indicatorColor}
                            {...getNodeTestIdProp(
                                `hourly-activity-tooltip-indicator-${index}`,
                            )}
                        />
                        <ChartTooltip.Label
                            {...getNodeTestIdProp(
                                `hourly-activity-tooltip-label-${index}`,
                            )}
                        >
                            {label}
                        </ChartTooltip.Label>
                        <ChartTooltip.Value
                            {...getNodeTestIdProp(
                                `hourly-activity-tooltip-value-${index}`,
                            )}
                        >
                            {valueFormatter
                                ? valueFormatter(entry.value ?? '')
                                : entry.value}
                        </ChartTooltip.Value>
                    </ChartTooltip.Item>
                );
            })}
        </ChartTooltip>
    );
};
